import { useRef } from "react";
import {
    motion,
    useReducedMotion,
    useScroll,
    useTransform,
} from "framer-motion";
import { BookPill } from "./BookPill";
import { Doodles } from "./Doodles";
import "./Hero.css";

const TICKER_ITEMS = [
    "Channel Strategy",
    "Thumbnails",
    "Titles & Hooks",
    "Scripting",
    "Long-form Editing",
    "Shorts & Reels",
    "Animation",
    "Publishing",
];

const LINE_COUNT = 20;

const fadeProps = (reduce, delay) => ({
    initial: reduce ? false : { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut", delay },
});

export const Hero = () => {
    const reduce = useReducedMotion();
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const linesY = useTransform(scrollYProgress, [0, 1], [0, 70]);

    const scrollToWork = () => {
        const el = document.getElementById("clips");
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
        else el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            className="hero"
            id="top"
            ref={sectionRef}
            data-testid="hero-section"
        >
            <motion.div
                className="hero-parallax"
                style={reduce ? undefined : { y: linesY }}
                aria-hidden="true"
            >
                <div className="hero-lines hero-lines-left">
                    {Array.from({ length: LINE_COUNT }).map((_, i) => (
                        <span
                            key={`l-${i}`}
                            className="hero-line"
                            style={{
                                width: `${60 + i * 10}px`,
                                animationDelay: `${i * 0.25}s`,
                            }}
                        />
                    ))}
                </div>
                <div className="hero-lines hero-lines-right">
                    {Array.from({ length: LINE_COUNT }).map((_, i) => (
                        <span
                            key={`r-${i}`}
                            className="hero-line"
                            style={{
                                width: `${60 + i * 10}px`,
                                animationDelay: `${i * 0.25}s`,
                            }}
                        />
                    ))}
                </div>
                <div className="hero-lines hero-lines-top">
                    {Array.from({ length: LINE_COUNT }).map((_, i) => (
                        <span
                            key={`t-${i}`}
                            className="hero-line"
                            style={{
                                height: `${60 + i * 10}px`,
                                animationDelay: `${i * 0.25}s`,
                            }}
                        />
                    ))}
                </div>
            </motion.div>

            <Doodles
                items={[
                    {
                        type: "sparkle",
                        color: "pink",
                        size: 28,
                        style: { top: "18%", left: "12%" },
                    },
                    {
                        type: "ring",
                        color: "blue",
                        size: 26,
                        style: { top: "24%", right: "14%" },
                        delay: "0.8s",
                    },
                    {
                        type: "plus",
                        color: "green",
                        size: 20,
                        style: { bottom: "28%", left: "18%" },
                        delay: "1.6s",
                    },
                    {
                        type: "squiggle",
                        color: "pink",
                        size: 44,
                        style: { bottom: "24%", right: "15%" },
                        delay: "0.4s",
                    },
                    {
                        type: "sparkle",
                        color: "yellow",
                        size: 18,
                        style: { top: "54%", left: "6%" },
                        delay: "2s",
                    },
                ]}
            />

            <div className="hero-content">
                <motion.div
                    className="hero-ticker"
                    data-testid="hero-ticker"
                    initial={reduce ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    <div className="hero-ticker-track">
                        {Array.from({ length: 4 }).flatMap((_, copy) =>
                            TICKER_ITEMS.map((item) => (
                                <span
                                    key={`${copy}-${item}`}
                                    className="tag-pill"
                                >
                                    {item}
                                </span>
                            )),
                        )}
                    </div>
                </motion.div>

                {/* Alternate headlines for A/B swaps:
                    Grow on YouTube without <span class="serif">growing</span> your team.
                    YouTube growth, <span class="serif">done for you</span>. */}
                <h1 className="hero-title" data-testid="hero-title">
                    {[
                        "You hit record.",
                        "We grow the",
                        "channel.",
                    ].map((line, i) => (
                        <span className="hero-title-line" key={line}>
                            <motion.span
                                className="hero-title-line-inner"
                                initial={reduce ? false : { y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{
                                    duration: 0.9,
                                    ease: [0.16, 1, 0.3, 1],
                                    delay: 0.15 + i * 0.12,
                                }}
                            >
                                {i === 2 ? (
                                    <span className="serif">
                                        channe
                                        <span className="serif-upright">l</span>
                                    </span>
                                ) : (
                                    line
                                )}
                                {i === 2 ? "." : ""}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    className="hero-subtitle"
                    data-testid="hero-subtitle"
                    {...fadeProps(reduce, 0.6)}
                >
                    Post-production and distribution for founders and brands
                    who want a serious social presence without building a
                    production team.{" "}
                    <span className="hero-subtitle-tags">
                        Retention Driven Work <em>|</em> Fast Human Edits{" "}
                        <em>|</em> Economic Cost
                    </span>
                </motion.p>

                <motion.div
                    className="hero-cta-row"
                    {...fadeProps(reduce, 0.75)}
                >
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={scrollToWork}
                        data-testid="hero-see-work-button"
                    >
                        See the work
                    </button>
                    <BookPill testid="hero-book-pill" primary="Free Consultation" />
                </motion.div>
            </div>

            <div className="hero-blur" aria-hidden="true" />
        </section>
    );
};
